/**
 * OfferCards — the "cards" offers layout (desktop only). Instead of stacking
 * every property on one page, the offers are shown as a responsive grid of
 * result cards; clicking a card opens that single property's full section (the
 * parent swaps to a one-property view with a "back to results" link).
 *
 * Mobile never renders this — ResalesLanding forces the list layout below the
 * desktop breakpoint, matching the "only change the desktop format" brief.
 *
 * Props:
 *   - offers   {Array}            — offer view-models (uses gallery, priceLabel,
 *                                   beds/baths, stats, title parts)
 *   - onSelect {(i:number)=>void} — open property i
 */
import React from 'react';
import { useStrings, useLocalization, propertyTitle } from './i18n';
import { cdnSize } from './mapProperty';
import { IconBed, IconBath, IconExpand } from './icons';
import Money from './Money';
import StatusRibbon from './StatusRibbon';

export default function OfferCards({ offers, onSelect }) {
    const t = useStrings();
    const l = useLocalization();
    const countRange = (from, to) => (to > from ? `${from} - ${to}` : `${from}`);

    return (
        <section className="rs-cards" aria-label={t.propertyOffers}>
            {offers.map((offer, i) => {
                const sizeVal = offer.stats.build.from || offer.stats.intFloor.from;
                const pricePeriod = offer.isRental ? (offer.isShortTerm ? t.perWeek : t.perMonth) : '';
                return (
                    <button
                        key={offer.id}
                        type="button"
                        className="rs-card-item"
                        onClick={() => onSelect(i)}
                        aria-label={`${t.viewProperty} — ${propertyTitle(l, offer)}`}
                    >
                        <div className="rs-card-item__media">
                            <img
                                className="rs-card-item__img"
                                src={cdnSize(offer.gallery[0], 'w800')}
                                alt=""
                                loading="lazy"
                            />
                            <span className="rs-card-item__num">{i + 1}</span>
                            {/* Sale Agreed / Under Offer corner banner (top-left;
                                the number badge sits top-right, glass at bottom). */}
                            <StatusRibbon statusBanner={offer.statusBanner} l={l} unavailable={offer.isUnavailable} />
                            {/* Glass panel over the photo — type/location only. */}
                            <div className="rs-card-item__glass">{propertyTitle(l, offer)}</div>
                        </div>
                        {/* White body: price + beds/baths/built chips (styled like
                            the mobile new-development unit-card chips). */}
                        <div className="rs-card-item__body">
                            <div className="rs-card-item__priceline">
                                {offer.isUnavailable ? (
                                    <div className="rs-card-item__price rs-unavailable">{t.unitUnavailable}</div>
                                ) : (
                                    <div className="rs-card-item__price">
                                        <Money>{offer.priceLabel}</Money>
                                        {pricePeriod && <span className="rs-price__period">{pricePeriod}</span>}
                                    </div>
                                )}
                                {offer.reference ? (
                                    <span className="rs-card-item__ref">{t.referenceShort}: {offer.reference}</span>
                                ) : null}
                            </div>
                            {!offer.isUnavailable && <div className="rs-card-item__specs">
                                <span className="rs-card-item__spec"><IconBed size={18} /> {countRange(offer.beds, offer.bedsTo)}</span>
                                <span className="rs-card-item__spec"><IconBath size={18} /> {countRange(offer.baths, offer.bathsTo)}</span>
                                {sizeVal ? (
                                    <span className="rs-card-item__spec"><IconExpand size={16} /> {sizeVal} {offer.unit}</span>
                                ) : null}
                            </div>}
                        </div>
                    </button>
                );
            })}
        </section>
    );
}
